import { useEffect, useRef } from 'react'

/**
 * Live input level of a MediaStreamTrack (board m5gc #20/#21): an
 * AnalyserNode on the track, sampled once per animation frame. Nothing here
 * goes through React state per frame — the caller gets a number and writes
 * it to a CSS variable or a ref; only the speaking edge (below) is worth a
 * render.
 */

/** Time-domain RMS of the analyser, 0..1 (speech is ~0.05–0.3, silence < 0.01). */
export function rmsLevel(samples: Float32Array): number {
  let sum = 0
  for (let i = 0; i < samples.length; i++) sum += samples[i]! * samples[i]!
  return Math.min(1, Math.sqrt(sum / Math.max(1, samples.length)))
}

/**
 * Speaking with hysteresis, so a ring does not flicker on every syllable:
 * on once the level has been above `on` for `attackMs`, off once it has
 * been below `off` for `releaseMs`. Pure — `next` is called per sample.
 */
export interface SpeakingOptions {
  on: number
  off: number
  attackMs: number
  releaseMs: number
}

export const SPEAKING_DEFAULTS: SpeakingOptions = {
  on: 0.06,
  off: 0.03,
  attackMs: 80,
  releaseMs: 600,
}

export interface SpeakingState {
  speaking: boolean
  /** When the level first crossed `on` (while not speaking) / `off` (while speaking); null = not pending. */
  since: number | null
}

export function nextSpeaking(
  state: SpeakingState,
  level: number,
  nowMs: number,
  options: SpeakingOptions = SPEAKING_DEFAULTS
): SpeakingState {
  if (!state.speaking) {
    if (level < options.on) return state.since === null ? state : { speaking: false, since: null }
    const since = state.since ?? nowMs
    return nowMs - since >= options.attackMs
      ? { speaking: true, since: null }
      : { speaking: false, since }
  }
  if (level > options.off) return state.since === null ? state : { speaking: true, since: null }
  const since = state.since ?? nowMs
  return nowMs - since >= options.releaseMs
    ? { speaking: false, since: null }
    : { speaking: true, since }
}

/**
 * Sample a track's level on every frame. `onLevel(level)` is called per
 * frame (write a CSS var, no state); `onSpeaking(bool)` only on the edge.
 * Both are read through refs so a new closure never restarts the analyser.
 * Nothing runs — and `onLevel(0)` is sent once — while `enabled` is false.
 */
export function useAudioLevel(
  track: MediaStreamTrack | null | undefined,
  enabled: boolean,
  callbacks: { onLevel?: (level: number) => void; onSpeaking?: (speaking: boolean) => void }
): void {
  const callbacksRef = useRef(callbacks)
  callbacksRef.current = callbacks

  useEffect(() => {
    if (!track || !enabled || typeof AudioContext === 'undefined') {
      callbacksRef.current.onLevel?.(0)
      callbacksRef.current.onSpeaking?.(false)
      return
    }
    let context: AudioContext
    let source: MediaStreamAudioSourceNode
    try {
      context = new AudioContext()
      source = context.createMediaStreamSource(new MediaStream([track]))
    } catch (err) {
      console.warn('[meet] audio level unavailable', err)
      return
    }
    const analyser = context.createAnalyser()
    analyser.fftSize = 512
    analyser.smoothingTimeConstant = 0.5
    source.connect(analyser)
    const buffer = new Float32Array(analyser.fftSize)
    let state: SpeakingState = { speaking: false, since: null }
    let frame = 0
    const tick = () => {
      analyser.getFloatTimeDomainData(buffer)
      const level = rmsLevel(buffer)
      callbacksRef.current.onLevel?.(level)
      const next = nextSpeaking(state, level, performance.now())
      if (next.speaking !== state.speaking) callbacksRef.current.onSpeaking?.(next.speaking)
      state = next
      frame = requestAnimationFrame(tick)
    }
    // A suspended context (autoplay policy) still needs a nudge on some browsers.
    void context.resume().catch(() => {})
    frame = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(frame)
      source.disconnect()
      void context.close().catch(() => {})
      callbacksRef.current.onLevel?.(0)
      callbacksRef.current.onSpeaking?.(false)
    }
  }, [track, enabled])
}
